import { lazy, Suspense, useCallback, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { App as AntApp } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ModuleBase } from '@fms/shared/router';
import { AuthProvider, usePermissions, createAuthAxios } from '@zero/auth-client';
import { LoginForm } from '@zero/auth-admin-ui';
import { RequireAuth, SessionGate, PreAuthToolbar, setSharedAxios, useEnabledModules, moduleUrl, firstModule } from '@fms/shared';

import { AdminLayout } from './components/AdminLayout.jsx';

/* ===== MODULE CHẠY NHÚNG (một SPA duy nhất) =====
   Chuyển module KHÔNG tải lại trang nữa: giữ nguyên access token trong bộ nhớ (đỡ một vòng
   /api/auth/refresh mỗi lần chuyển), giữ cache TanStack, và thư viện dùng chung (React,
   AntD, AG-Grid) chỉ tải MỘT lần thay vì mỗi module một bản.
   Mã nguồn vẫn tách theo module như cũ — ở đây chỉ nạp lười phần route của nó.
   Đợt này mới gộp Chi phí + Danh mục (hai module KHÔNG có bản tàu, nên không vướng cờ
   `IS_SHIP_MODULE` vốn đang tính lúc build). Các module còn lại vẫn là SPA riêng. */
const CostModule     = lazy(() => import('../../cost/src/hosted.jsx'));
const MasterModule   = lazy(() => import('../../master-data/src/hosted.jsx'));
const PurcModule     = lazy(() => import('../../purchase/src/hosted.jsx'));
const NoonModule     = lazy(() => import('../../noon-report/src/hosted.jsx'));
const ShipNoonModule = lazy(() => import('../../shipnoon/src/hosted.jsx'));
/* Bảo trì và Hồ sơ tàu: MỘT chunk dùng cho CẢ hai biến thể (văn phòng + tàu) — khác nhau
   đúng prop `scope`, xem `datPhamVi` trong moduleScope.js của chúng. */
const PmsModule      = lazy(() => import('../../pms/src/hosted.jsx'));
const CertModule     = lazy(() => import('../../certificate/src/hosted.jsx'));

/* MỖI NHÁNH MODULE MỘT KHO CACHE RIÊNG, không dùng chung một QueryClient.
   Lý do: bản văn phòng và bản tàu của Bảo trì / Hồ sơ tàu dùng CHUNG mã nguồn nên khoá truy
   vấn giống hệt nhau (vd ['pms','ships-select']) — chung kho là dữ liệu tàu hiện sang văn
   phòng và ngược lại. Tách theo tiền tố đường dẫn thì vẫn giữ được cache khi quay lại module
   (đó là cái làm việc chuyển module thành tức thì) mà không lẫn dữ liệu. */
const khoCache = new Map();
function layKhoCache(base) {
  if (!khoCache.has(base)) {
    khoCache.set(base, new QueryClient({
      defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
    }));
  }
  return khoCache.get(base);
}

/* Khung tạm trong lúc chunk của module đang tải — dùng lại đúng khung mà index.html vẽ khi
   tải trang, để mắt không thấy nhảy. */
function KhungTam() {
  return (
    <div id="fms-boot" aria-hidden="true">
      <div className="b-sider" />
      <div className="b-main"><div className="b-header" /></div>
    </div>
  );
}

/** Bọc một module nhúng: tiền tố đường dẫn + kho cache riêng + chỗ chờ khi nạp chunk. */
function ModuleHost({ base, children }) {
  return (
    <ModuleBase base={base}>
      <QueryClientProvider client={layKhoCache(base)}>
        <Suspense fallback={<KhungTam />}>{children}</Suspense>
      </QueryClientProvider>
    </ModuleBase>
  );
}

import { DashboardPage }      from './pages/admin/DashboardPage.jsx';
import { UsersPage }          from './pages/admin/UsersPage.jsx';
import { RolesPage }          from './pages/admin/RolesPage.jsx';
import { PermissionsPage }    from './pages/admin/PermissionsPage.jsx';
import { PoliciesPage }       from './pages/admin/PoliciesPage.jsx';
import { AuditPage }          from './pages/admin/AuditPage.jsx';
import { AlertsPage }         from './pages/admin/AlertsPage.jsx';
import { ProfilePage }        from './pages/admin/ProfilePage.jsx';
import { SessionsPage }       from './pages/admin/SessionsPage.jsx';
import { BrandingPage }       from './pages/admin/BrandingPage.jsx';
import { SmtpConfigPage }     from './pages/admin/SmtpConfigPage.jsx';
import { GoogleOAuthPage }    from './pages/admin/GoogleOAuthPage.jsx';
import { SecurityConfigPage } from './pages/admin/SecurityConfigPage.jsx';
import { TicketConfigPage }   from './pages/admin/TicketConfigPage.jsx';

/* TRANG CHỦ FMS ĐÃ BỎ (user chốt 15/09): nó chỉ là bảng thẻ lặp lại đúng danh sách module
   mà sidebar đã liệt kê sẵn. `/fms/` nay chuyển thẳng sang module ĐẦU danh sách của bản cài
   này — cũng là nơi người dùng rơi vào sau khi đăng nhập. */
function ModuleRedirect() {
  const enabled = useEnabledModules();     // null = chưa tải xong /api/modules
  useEffect(() => {
    if (enabled === null) return;          // chờ biết bản cài này bật những module nào
    const m = firstModule(enabled);
    window.location.replace(moduleUrl(m.key, m.href));
  }, [enabled]);
  // Trang trung chuyển, hiện trong tích tắc — không dựng layout để khỏi nháy sidebar hai lần.
  return null;
}

function LoginPage() {
  const { login, loginWithGoogle, context } = usePermissions();
  const navigate = useNavigate();
  useEffect(() => { if (context) navigate('/'); }, [context, navigate]);
  return (
    <>
      <PreAuthToolbar />
      <LoginForm
        title="FMS VOSCO"
        subtitle="Fleet Management System"
        onLogin={({ username, password, totpCode }) => login({ username, password, totpCode })}
        onGoogleLogin={async (idToken) => loginWithGoogle({ idToken })}
      />
    </>
  );
}

// Admin dashboard fallback: user thiếu Auth.Audit.ViewStats → redirect /admin/profile.
function AdminDashboardGuard({ axios }) {
  const { has } = usePermissions();
  if (!has('Auth.Audit.ViewStats')) return <Navigate to="/admin/profile" replace />;
  return <DashboardPage axios={axios} />;
}

export function AppShell() {
  const navigate = useNavigate();
  const handleLogout = useCallback(() => navigate('/login'), [navigate]);
  const axios = useMemo(() => createAuthAxios({ baseURL: '', onLogout: handleLogout }), [handleLogout]);
  // Shell TRUOC DAY khong goi setSharedAxios (4 SPA kia deu co) -> util dung chung cua
  // @fms/shared (badge ticket, FMSGrid grid-columns) khong co axios de goi API.
  useEffect(() => setSharedAxios(axios), [axios]);

  return (
    <AntApp>
      <AuthProvider axios={axios} onAuthError={handleLogout}>
        <SessionGate>
          <Routes>
            {/* Pre-auth: no layout */}
            <Route path="/login" element={<LoginPage />} />

            {/* `/` và `/dashboard` cũ: chuyển sang module đầu danh sách (không còn trang chủ) */}
            <Route path="/"          element={<RequireAuth><ModuleRedirect /></RequireAuth>} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />

            {/* Admin AuthSystem: AdminLayout, mount /admin/* */}
            <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
              <Route index               element={<AdminDashboardGuard axios={axios} />} />
              <Route path="users"        element={<UsersPage          axios={axios} />} />
              <Route path="roles"        element={<RolesPage          axios={axios} />} />
              <Route path="permissions"  element={<PermissionsPage    axios={axios} />} />
              <Route path="policies"     element={<PoliciesPage       axios={axios} />} />
              <Route path="audit"        element={<AuditPage          axios={axios} />} />
              <Route path="alerts"       element={<AlertsPage         axios={axios} />} />
              <Route path="profile"      element={<ProfilePage        axios={axios} />} />
              <Route path="sessions"     element={<SessionsPage       axios={axios} />} />
              <Route path="branding"     element={<BrandingPage       axios={axios} />} />
              <Route path="smtp"         element={<SmtpConfigPage     axios={axios} />} />
              <Route path="google-oauth" element={<GoogleOAuthPage    axios={axios} />} />
              <Route path="security"     element={<SecurityConfigPage axios={axios} />} />
              <Route path="ticket"       element={<TicketConfigPage   axios={axios} />} />
            </Route>

            {/* Module nhúng — đường dẫn giữ NGUYÊN như khi còn là SPA riêng (/fms/cost/…,
                /fms/master/…) nên không ảnh hưởng link cũ, dấu trang hay `lastRoute`. */}
            <Route path="/noon/*"     element={<ModuleHost base="/noon"><NoonModule axios={axios} /></ModuleHost>} />
            <Route path="/shipnoon/*" element={<ModuleHost base="/shipnoon"><ShipNoonModule axios={axios} /></ModuleHost>} />
            <Route path="/pms/*"      element={<ModuleHost base="/pms"><PmsModule axios={axios} scope="office" /></ModuleHost>} />
            <Route path="/shippms/*"  element={<ModuleHost base="/shippms"><PmsModule axios={axios} scope="ship" /></ModuleHost>} />
            <Route path="/purc/*"     element={<ModuleHost base="/purc"><PurcModule axios={axios} /></ModuleHost>} />
            <Route path="/cost/*"     element={<ModuleHost base="/cost"><CostModule axios={axios} /></ModuleHost>} />
            <Route path="/cert/*"     element={<ModuleHost base="/cert"><CertModule axios={axios} scope="office" /></ModuleHost>} />
            <Route path="/shipcert/*" element={<ModuleHost base="/shipcert"><CertModule axios={axios} scope="ship" /></ModuleHost>} />
            <Route path="/master/*"   element={<ModuleHost base="/master"><MasterModule axios={axios} /></ModuleHost>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SessionGate>
      </AuthProvider>
    </AntApp>
  );
}
